<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Prep Task Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <basic-heading
      :title="`${prep_process.name}: ${prep_task.name}`"
      class="mb-3"
    >
      <template #right-content>
        <header-button-group :button_config="button_config">
          <template #post_buttons>
            <button
              v-show="checkPermission('delete prep task')"
              title="Delete analyte"
              class="btn btn-danger ml-1"
              @click="showDeleteModal"
            >
              <i class="fal fa-trash" />
            </button>
          </template>
        </header-button-group>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read prep task'">
      <template #gated-content>
        <b-row>
          <b-col class="pr-0">
            <prep-task-card :prep_task="prep_task" />
          </b-col>
          <b-col v-if="links.length">
            <link-tile
              v-for="(link, linkIndex) in links"
              :key="linkIndex"
              icon-cols="2"
              :to="link.to"
              :fa-icon="link.faIcon"
              :variant="link.variant"
              :text="link.text"
            />
          </b-col>
        </b-row>
        <prep-task-detail-cardstack :prep_task="prep_task" />
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import LinkTile from "@/components/shared/LinkTile";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import PrepTaskCard from "@/components/lab/preptask/PrepTaskCard";
import PrepTaskDetailCardstack from "@/components/lab/preptask/PrepTaskDetailCardstack";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getModel } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { notificationOptions } from "@/shared/toast-notification-options";
import { prepTaskNotifications } from "@/shared/notifications";

const PrepTaskRepository = LabRepositoryFactory.get("prepTask");

Vue.use(VueNotifications, notificationOptions);

export default {
  name: "PrepTaskDetail",
  components: {
    BasicHeading,
    HeaderButtonGroup,
    LinkTile,
    PermissionAlertGate,
    PrepTaskCard,
    PrepTaskDetailCardstack,
  },
  data() {
    return {
      loading: true,
      prep_task_id: this.$route.params.prep_task_id || null,
      prep_task: null,
      prep_process: null,
      links: [],
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            linkto: `/prep-processes/${this.prep_process.id}`,
            title: "Prep Task list",
          },
          {
            icon: "fa-pencil",
            linkto: `/prep-tasks/${this.prep_task_id}/edit`,
            title: "Edit Prep Task",
            variant: "warning",
            permission: this.checkPermission("edit prep task"),
          },
        ],
      };
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    async init() {
      const payload = {
        message: this.$bvToast,
      };

      this.prep_task = await getModel({
        ...payload,
        model_type: "prep_task",
        id: this.prep_task_id,
      });
      this.prep_process = await getModel({
        ...payload,
        model_type: "prep_process",
        id: this.prep_task.prep_process_id,
      });

      this.links = [
        ...this.links,
        {
          to: `/prep-processes/${this.prep_process.id}`,
          faIcon: this.prep_process.icon(),
          variant: "dark",
          text: this.prep_process.name,
        },
      ];
      this.loading = false;
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this prep task. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await PrepTaskRepository.suspend(this.prep_task_id);
  
              this.deleteSuccess();
  
              this.$router.push(`/prep-processes/${this.prep_process.id}`);
            } catch ({ response }) {
              this.$bvToast.toast(response.data.error, {
                title: "Task delete Failed.",
                variant: "danger",
              });
            }
          }
        });
    },
  },
  created() {
    this.loading = true;

    this.init();
  },
  notifications: prepTaskNotifications,
};
</script>
